import React, { useState } from "react";
import { Button, Col, Form } from "react-bootstrap";
import { Dropdown } from "primereact/dropdown";
import FieldHelpTooltip from "../../../../components/Help/FieldHelpTooltip";
import { HELP_CONTENT } from "../../../../components/Help/HelpContent";
import { isPaidPaymentStatus } from "../../../../utils/paymentStatus";
import {
  DISCOUNT_FLAT,
  DISCOUNT_PCT,
  lineKey,
  pricingProblems,
  toNum
} from "../bookingPricing";
import { paymentMethodsFor } from "../useBookingCheckout";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
const TIP_CHOICES = [
  { label: "None", val: 0 },
  { label: "10%", val: 10 },
  { label: "15%", val: 15 },
  { label: "20%", val: 20 }
];

// A flat / % switch with an amount, used for the order discount and for a service line.
// The type is a real switch: one track, both choices always visible, the active one a raised pill.
function DiscountInput({ spec, onChange, ariaLabel, compact, currency }) {
  const type = spec?.type === DISCOUNT_PCT ? DISCOUNT_PCT : DISCOUNT_FLAT;
  const options = [
    [DISCOUNT_FLAT, currency || "Flat"],
    [DISCOUNT_PCT, "%"]
  ];
  const choose = (t) =>
    onChange({ type: t, value: type === t ? (spec?.value ?? "") : "" });
  const onKey = (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const other = type === DISCOUNT_FLAT ? DISCOUNT_PCT : DISCOUNT_FLAT;
    choose(other);
    const radios =
      e.currentTarget.parentElement.querySelectorAll('[role="radio"]');
    radios[other === DISCOUNT_FLAT ? 0 : 1]?.focus();
  };
  return (
    <div
      className={`bstep-discount${compact ? " is-compact" : ""}`}
      role="group"
      aria-label={ariaLabel}
    >
      <div className="bstep-discount-row">
        <div
          className="bstep-switch"
          role="radiogroup"
          aria-label={`${ariaLabel} type`}
          data-active={type}
        >
          <span className="bstep-switch-thumb" aria-hidden="true" />
          {options.map(([t, label]) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={type === t}
              tabIndex={type === t ? 0 : -1}
              className={`bstep-switch-opt${type === t ? " is-active" : ""}`}
              onClick={() => choose(t)}
              onKeyDown={onKey}
            >
              {label}
            </button>
          ))}
        </div>
        <Form.Control
          type="number"
          min={0}
          max={type === DISCOUNT_PCT ? 100 : undefined}
          step="any"
          size="sm"
          aria-label={`${ariaLabel} amount`}
          style={{ width: compact ? 84 : 110 }}
          value={spec?.value ?? ""}
          placeholder="0"
          onChange={(e) => {
            let v = e.target.value;
            if (v !== "" && toNum(v) < 0) v = "0";
            if (type === DISCOUNT_PCT && toNum(v) > 100) v = "100";
            onChange({ type, value: v });
          }}
        />
      </div>
      <div className="bstep-discount-hint">
        {type === DISCOUNT_PCT ? "Percent of the subtotal" : "Flat amount"}
      </div>
    </div>
  );
}

// Step 4: the price, the payment and - for a saved booking - its status.
function ReviewStep({ b, formik }) {
  const {
    isEditing,
    isSportsService,
    currency,
    taxPercentage,
    pricing,
    priceSection,
    serviceData,
    lineDiscounts,
    setLineDiscount,
    lineQty,
    setLineQuantity,
    discountSpec,
    setDiscountSpec,
    couponOptions,
    activeCoupon,
    setCouponId,
    tipPercent,
    setTipPercent,
    addCustomService,
    customNameChange,
    customPriceChange,
    deleteCustomItem,
    priceChange,
    paidAmount,
    setPaidAmount,
    balanceDue,
    totalAmount,
    bookingStatus,
    paymentStatus,
    selectedPayment,
    setSelectedPayment,
    setShowOnlineDisabledModal,
    cashPaymentStatus,
    setCashPaymentStatus,
    pendingBalanceIncrease
  } = b;
  const { values, errors, touched, setFieldValue, handleBlur } = formik;
  const [openLineDiscounts, setOpenLineDiscounts] = useState({});

  const problems = pricingProblems({ result: pricing, paidAmount });
  const overpaid =
    pricing.hasContent && toNum(paidAmount) > totalAmount + 0.005;
  const hasLines = priceSection.some(
    (r) =>
      r &&
      !["Discount", "Total"].includes(r.name) &&
      !/^tax/i.test(r.name || "")
  );

  return (
    <>
      {b.providerOptional && b.slotOverrideNote && (
        <div
          className="bstep-slot-note is-warning"
          role="alert"
          data-testid="review-slot-note"
        >
          {b.slotOverrideNote}
        </div>
      )}
      <div className="bstep-card" data-tour="booking-pricing">
        <div className="bstep-card-head d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h5>Price</h5>
            <p>
              Check the amounts. Prices, discounts and extra items can be
              changed here.
            </p>
          </div>
          <Button
            type="button"
            variant="outline-danger"
            size="sm"
            onClick={addCustomService}
          >
            + Add item
          </Button>
        </div>

        {!hasLines && (
          <div className="bstep-empty">
            Nothing to charge yet - add a service in the Service step.
          </div>
        )}

        {hasLines && (
          <div
            className="bstep-lines"
            role="table"
            aria-label="Price breakdown"
          >
            {priceSection.map((row, idx) => {
              const n = (row?.name || "").toLowerCase();
              if (
                row?.name === "Discount" ||
                row?.name === "Total" ||
                n.startsWith("tax")
              )
                return null;

              if (row?.isPart) {
                return (
                  <div
                    className="bstep-line"
                    key={row.id || row.name}
                    role="row"
                  >
                    <div className="bstep-line-name">
                      {row.name}
                      <span className="bstep-muted">
                        {" "}
                        · {row.quantity} {row.unit_of_measure} × {currency}{" "}
                        {fmt(row.unit_price)}
                      </span>
                    </div>
                    <div className="bstep-line-amount">
                      {currency} {fmt(row.amount)}
                    </div>
                  </div>
                );
              }

              if (row?.isCustom) {
                return (
                  <div
                    className="bstep-line"
                    key={row.id || row.name || idx}
                    role="row"
                  >
                    <Form.Control
                      type="text"
                      placeholder="E.g. Tips,Material,Extra Work"
                      aria-label="Item name"
                      value={row?.name}
                      size="sm"
                      className="bstep-line-name-input"
                      onChange={(e) => customNameChange(e.target.value, idx)}
                    />
                    <div className="bstep-line-controls">
                      <span className="bstep-muted">{currency}</span>
                      <Form.Control
                        type="number"
                        aria-label="Item amount"
                        value={row?.amount}
                        size="sm"
                        style={{ width: 110 }}
                        onChange={(e) => customPriceChange(e.target.value, idx)}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        aria-label="Remove item"
                        onClick={() => deleteCustomItem(idx)}
                      >
                        ✕
                      </Button>
                    </div>
                  </div>
                );
              }

              // A service line
              const offering = serviceData.find(
                (s) => s.service_offering_id === row.service_offering_id
              );
              const key = lineKey(row);
              const qty = isEditing
                ? 1
                : Math.max(1, Math.floor(toNum(lineQty[key])) || 1);
              const ld = lineDiscounts[key];
              const lineOpen = openLineDiscounts[key] || toNum(ld?.value) > 0;
              return (
                <div
                  className="bstep-line is-service"
                  key={row.name}
                  role="row"
                >
                  <div className="bstep-line-name">{row.name}</div>
                  <div className="bstep-line-controls">
                    {!isEditing && (
                      <div
                        className="bstep-qty"
                        role="group"
                        aria-label="Quantity"
                      >
                        <button
                          type="button"
                          aria-label="Decrease quantity"
                          disabled={qty <= 1}
                          onClick={() => setLineQuantity(key, qty - 1)}
                        >
                          −
                        </button>
                        <span aria-live="polite">{qty}</span>
                        <button
                          type="button"
                          aria-label="Increase quantity"
                          onClick={() => setLineQuantity(key, qty + 1)}
                        >
                          +
                        </button>
                      </div>
                    )}
                    <span className="bstep-muted">{currency}</span>
                    <Form.Control
                      type="number"
                      aria-label={`${row.name} price`}
                      value={offering?.amount ?? ""}
                      size="sm"
                      style={{ width: 110 }}
                      onChange={(e) => priceChange(e.target.value, row)}
                    />
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      aria-expanded={lineOpen}
                      onClick={() =>
                        setOpenLineDiscounts((prev) => ({
                          ...prev,
                          [key]: !lineOpen
                        }))
                      }
                    >
                      Discount
                    </button>
                  </div>
                  {lineOpen && (
                    <div className="bstep-line-sub">
                      <DiscountInput
                        compact
                        currency={currency}
                        ariaLabel={`${row.name} discount`}
                        spec={ld}
                        onChange={(spec) => setLineDiscount(key, spec)}
                      />
                      <div className="bstep-line-sub-note">
                        Discount on this item only. The subtotal is after item
                        discounts; the Discount below is an extra overall
                        discount.
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Totals */}
        {pricing.hasContent && (
          <div className="bstep-totals">
            <div className="bstep-total-row">
              <span>Subtotal</span>
              <span>
                {currency} {fmt(pricing.subtotal)}
              </span>
            </div>

            {!isEditing && couponOptions.length > 0 && (
              <div className="bstep-coupon">
                <div className="bstep-total-row">
                  <span>Coupon</span>
                  <Dropdown
                    aria-label="Coupon"
                    value={activeCoupon?.id ?? null}
                    options={couponOptions.map((c) => ({
                      label: `${c.name} (${Number(c.discount_percent)}%)`,
                      value: c.id
                    }))}
                    placeholder="Select coupon"
                    showClear
                    filter
                    filterPlaceholder="Search coupon"
                    emptyFilterMessage="No coupon found"
                    onChange={(e) => setCouponId(e.value ?? null)}
                    className="bstep-coupon-dd"
                    style={{
                      width: 260,
                      height: "40px",
                      backgroundColor: "white",
                      border: "1px solid #E9ECEF"
                    }}
                  />
                </div>
                {activeCoupon && (
                  <div className="bstep-coupon-applied" role="status">
                    <span className="bstep-coupon-check" aria-hidden="true">
                      ✓
                    </span>
                    <div className="bstep-coupon-text">
                      <strong>{activeCoupon.name}</strong> applied
                      <span className="bstep-coupon-sub">
                        {" "}
                        · {Number(activeCoupon.discount_percent)}% off
                      </span>
                    </div>
                    <div className="bstep-coupon-amt">
                      −{currency} {fmt(pricing.couponAmount)}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bstep-total-row is-discount">
              <span>Discount</span>
              <span className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                <DiscountInput
                  ariaLabel="Order discount"
                  spec={discountSpec}
                  onChange={setDiscountSpec}
                  currency={currency}
                />
                <span
                  className={
                    pricing.discountAmount - pricing.couponAmount > 0
                      ? "text-success"
                      : "bstep-muted"
                  }
                >
                  −{currency}{" "}
                  {fmt(pricing.discountAmount - pricing.couponAmount)}
                </span>
              </span>
            </div>

            <div className="bstep-total-row">
              <span>Tax ({taxPercentage}%)</span>
              <span>
                {currency} {fmt(pricing.taxAmount)}
              </span>
            </div>

            {!isEditing && (
              <div className="bstep-total-row">
                <span>Tip</span>
                <span className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                  <span
                    className="btn-group btn-group-sm"
                    role="group"
                    aria-label="Tip"
                  >
                    {TIP_CHOICES.map((t) => (
                      <button
                        key={t.val}
                        type="button"
                        aria-pressed={tipPercent === t.val}
                        className={`btn ${tipPercent === t.val ? "btn-danger" : "btn-outline-secondary"}`}
                        onClick={() => setTipPercent(t.val)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </span>
                  {pricing.tipAmount > 0 && (
                    <span>
                      {currency} {fmt(pricing.tipAmount)}
                    </span>
                  )}
                </span>
              </div>
            )}

            <div className="bstep-total-row is-grand">
              <span>Total</span>
              <span>
                {currency} {fmt(pricing.total)}
              </span>
            </div>
          </div>
        )}

        {problems.map((p) => (
          <div
            key={p}
            className="alert alert-danger py-2 mt-3 mb-0"
            role="alert"
            style={{ fontSize: 13 }}
          >
            {p}
          </div>
        ))}
        {isEditing && pendingBalanceIncrease > 0 && (
          <div
            className="alert alert-warning py-2 mt-3 mb-0"
            role="status"
            style={{ fontSize: 13 }}
          >
            Parts added in this visit increase the balance due by {currency}{" "}
            {fmt(pendingBalanceIncrease)}. Click Update to save the new total.
          </div>
        )}
      </div>

      {/* Payment / status */}
      <div className="bstep-card">
        <div className="bstep-card-head">
          <h5>Status & payment</h5>
        </div>

        <div className="row g-3 mb-3">
          <Col md={6}>
            <Form.Label>
              Booking Status
              <FieldHelpTooltip
                {...(isSportsService
                  ? HELP_CONTENT.bookings.Sportsstatus
                  : HELP_CONTENT.bookings.status)}
              />
            </Form.Label>
            <Dropdown
              name="status"
              value={values.status}
              options={bookingStatus?.map((s) => ({
                label: s.name,
                value: s.id
              }))}
              placeholder="Select Status"
              onChange={(e) => setFieldValue("status", e.value)}
              onBlur={handleBlur}
              className={`w-100 ${touched.status && errors.status ? "is-invalid" : ""}`}
              style={{
                height: "40px",
                backgroundColor: "white",
                border:
                  touched.status && errors.status
                    ? "1px solid #dc3545"
                    : "1px solid #E9ECEF"
              }}
            />
            {touched.status && errors.status && (
              <div className="invalid-feedback d-block">{errors.status}</div>
            )}
          </Col>
          {isEditing && (
            <Col md={6}>
              <Form.Label>
                Payment
                <FieldHelpTooltip
                  {...(isSportsService
                    ? HELP_CONTENT.bookings.SportspaymentStatus
                    : HELP_CONTENT.bookings.paymentStatus)}
                />
              </Form.Label>
              <Dropdown
                name="payment"
                value={values.payment}
                options={paymentStatus?.map((p) => ({
                  label: p.name,
                  value: p.id
                }))}
                placeholder="Select Payment"
                onChange={(e) => {
                  setFieldValue("payment", e.value);
                  const selectedPaymentName = paymentStatus?.find(
                    (p) => p.id === e.value
                  )?.name;
                  // A paid status means the whole total was received; anything else, nothing yet.
                  setPaidAmount(
                    isPaidPaymentStatus(selectedPaymentName) ? totalAmount : 0
                  );
                }}
                onBlur={handleBlur}
                className={`w-100 ${touched.payment && errors.payment ? "is-invalid" : ""}`}
                style={{
                  height: "40px",
                  backgroundColor: "white",
                  border:
                    touched.payment && errors.payment
                      ? "1px solid #dc3545"
                      : "1px solid #E9ECEF"
                }}
              />
              {touched.payment && errors.payment && (
                <div className="invalid-feedback d-block">{errors.payment}</div>
              )}
            </Col>
          )}
        </div>

        {!isEditing && (
          <>
            <div
              className="bstep-pay-methods"
              role="radiogroup"
              aria-label="Payment method"
            >
              {paymentMethodsFor(currency).map((m) => {
                const active = selectedPayment === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`bstep-pay-method ${active ? "is-active" : ""}`}
                    onClick={() => {
                      // Pay Online isn't enabled for vendors yet: explain instead of selecting.
                      if (m.id === "online") setShowOnlineDisabledModal(true);
                      else setSelectedPayment(m.id);
                    }}
                  >
                    <span className="bstep-pay-emoji" aria-hidden="true">
                      {m.emoji}
                    </span>
                    {m.label}
                  </button>
                );
              })}
            </div>

            {selectedPayment === "cash" && paymentStatus.length > 0 && (
              <div className="mt-3" style={{ maxWidth: 360 }}>
                <Form.Label>Payment Status</Form.Label>
                <Form.Select
                  aria-label="Payment status"
                  value={cashPaymentStatus ?? ""}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    setCashPaymentStatus(id);
                    // Same as the edit screen: a paid status means the whole total was received.
                    const selectedPaymentName = paymentStatus.find(
                      (p) => p.id === id
                    )?.name;
                    setPaidAmount(
                      isPaidPaymentStatus(selectedPaymentName) ? totalAmount : 0
                    );
                  }}
                  style={{ height: 40 }}
                >
                  {paymentStatus.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            )}
          </>
        )}

        {/* Paid / balance (both modes) */}
        <div className="bstep-paid">
          <div className="bstep-paid-row">
            <label htmlFor="bstep-paid">
              {isEditing ? "Paid Amount" : "Paid now"}
            </label>
            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
              <span
                className="btn-group btn-group-sm"
                role="group"
                aria-label="Quick amounts"
              >
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPaidAmount(0)}
                >
                  None
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() =>
                    setPaidAmount(Math.round((totalAmount / 2) * 100) / 100)
                  }
                >
                  Half
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPaidAmount(totalAmount)}
                >
                  Full
                </button>
              </span>
              <span className="bstep-muted">{currency}</span>
              <Form.Control
                id="bstep-paid"
                type="number"
                min={0}
                size="sm"
                style={{ width: 120 }}
                value={paidAmount === 0 ? "" : paidAmount}
                placeholder="0.00"
                onChange={(e) => setPaidAmount(Number(e.target.value) || 0)}
              />
            </div>
          </div>
          <div className="bstep-paid-row">
            <span>Balance Due</span>
            <strong className={balanceDue > 0 ? "text-danger" : "text-success"}>
              {currency} {fmt(balanceDue > 0 ? balanceDue : 0)}
            </strong>
          </div>
          {overpaid && (
            <div className="bstep-muted" style={{ fontSize: 12 }}>
              The paid amount is more than the total.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ReviewStep;
